/**
 * Veluna Quiz State Utility (TILE-01)
 * 
 * Provides a unified API for reading quiz state from multiple sources:
 * 1. Server-provided customer metafield (window.__VELUNA_CUSTOMER_QUIZ__)
 * 2. localStorage (veluna_quiz_results)
 * 3. URL parameters (recommended, bundle, from_quiz)
 * 
 * Returns canonical schema objects conforming to docs/VELUNA_QUIZ_SCHEMA.md
 */

(function() {
  'use strict';

  // Allowed enum values for validation
  const ALLOWED_OUTCOMES = ['mind-calm', 'deep-restore', 'circadian-reset', 'sacred-soothe', 'all'];
  const ALLOWED_BUNDLES = ['starter-set', 'booster-set', 'full-set'];
  const EXPIRATION_DAYS = 30;

  /**
   * Normalize legacy localStorage format to canonical schema
   * @param {Object} raw - Legacy quiz results object
   * @returns {Object|null} - Canonical schema object or null if invalid
   */
  function normalizeQuizState(raw) {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    try {
      // Extract and validate outcome_handle / recommended_category
      let outcomeHandle = raw.category || raw.outcome_handle || raw.recommended_category || 'all';
      if (!ALLOWED_OUTCOMES.includes(outcomeHandle)) {
        outcomeHandle = 'all';
      }

      // Extract and validate bundle_preference
      let bundlePreference = raw.bundleType || raw.bundle_preference || 'unknown';
      if (!ALLOWED_BUNDLES.includes(bundlePreference)) {
        bundlePreference = 'unknown';
      }

      // Extract sleep_issue_primary from answers if available
      let sleepIssuePrimary = 'unknown';
      if (raw.answers && typeof raw.answers === 'object') {
        // Try common answer field names
        if (raw.answers.primary_issue && raw.answers.primary_issue.value) {
          sleepIssuePrimary = String(raw.answers.primary_issue.value);
        } else if (raw.answers.sleep_issue && raw.answers.sleep_issue.value) {
          sleepIssuePrimary = String(raw.answers.sleep_issue.value);
        } else if (raw.answers.q1 && raw.answers.q1.value) {
          sleepIssuePrimary = String(raw.answers.q1.value);
        }
      }

      // Extract timestamp
      let quizTimestamp = raw.timestamp || raw.quiz_timestamp || Date.now();
      if (typeof quizTimestamp !== 'number' || quizTimestamp <= 0) {
        quizTimestamp = Date.now();
      }

      // Extract answers (preserve structure)
      const answers = (raw.answers && typeof raw.answers === 'object') ? raw.answers : {};

      // Extract benefits (must be array)
      let benefits = [];
      if (Array.isArray(raw.benefits)) {
        benefits = raw.benefits;
      } else if (raw.benefits && typeof raw.benefits === 'string') {
        benefits = [raw.benefits];
      }

      // Extract email (validate format if present)
      let email = null;
      if (raw.email && typeof raw.email === 'string' && raw.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(raw.email.trim())) {
          email = raw.email.trim();
        }
      }

      // Build canonical object
      return {
        schema_version: 1,
        outcome_handle: outcomeHandle,
        recommended_category: outcomeHandle, // Same as outcome_handle
        bundle_preference: bundlePreference,
        sleep_issue_primary: sleepIssuePrimary,
        quiz_timestamp: quizTimestamp,
        answers: answers,
        benefits: benefits,
        email: email
      };
    } catch (e) {
      console.warn('[VelunaQuizState] Error normalizing quiz state:', e);
      return null;
    }
  }

  /**
   * Check if quiz results are expired
   * @param {Object} quizState - Canonical quiz state object
   * @returns {boolean} - True if expired
   */
  function isExpired(quizState) {
    if (!quizState || !quizState.quiz_timestamp) {
      return true;
    }

    const daysSinceQuiz = (Date.now() - quizState.quiz_timestamp) / (1000 * 60 * 60 * 24);
    return daysSinceQuiz > EXPIRATION_DAYS;
  }

  /**
   * Read quiz state from server-provided metafield
   * @returns {Object|null} - Canonical quiz state or null
   */
  function readFromServer() {
    try {
      const serverData = window.__VELUNA_CUSTOMER_QUIZ__;
      if (!serverData || typeof serverData !== 'object') {
        return null;
      }

      // If already in canonical format, validate and return
      if (serverData.schema_version === 1) {
        // Validate required fields
        if (serverData.outcome_handle && serverData.quiz_timestamp) {
          const normalized = normalizeQuizState(serverData);
          return normalized && !isExpired(normalized) ? normalized : null;
        }
      }

      // Otherwise normalize from legacy format
      const normalized = normalizeQuizState(serverData);
      return normalized && !isExpired(normalized) ? normalized : null;
    } catch (e) {
      console.warn('[VelunaQuizState] Error reading from server:', e);
      return null;
    }
  }

  /**
   * Read quiz state from localStorage
   * @returns {Object|null} - Canonical quiz state or null
   */
  function readFromLocalStorage() {
    try {
      const stored = localStorage.getItem('veluna_quiz_results');
      if (!stored) {
        return null;
      }

      const raw = JSON.parse(stored);
      const normalized = normalizeQuizState(raw);
      return normalized && !isExpired(normalized) ? normalized : null;
    } catch (e) {
      // Silently fail - localStorage may be unavailable or corrupted
      return null;
    }
  }

  /**
   * Build minimal quiz state from URL parameters
   * @returns {Object|null} - Canonical quiz state or null
   */
  function readFromURL() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      // Support both 'recommended' and 'category' parameters (quiz uses 'category')
      const recommended = urlParams.get('recommended') || urlParams.get('category');
      const bundle = urlParams.get('bundle');

      // Only create state if we have at least a recommended category
      if (!recommended) {
        return null;
      }

      // Validate recommended category
      // Handle numeric category codes (legacy: 1=mind-calm, 2=deep-restore, 3=circadian-reset, 4=sacred-soothe)
      let outcomeHandle = recommended.toLowerCase();
      if (outcomeHandle === '1' || outcomeHandle === 'mind-calm') {
        outcomeHandle = 'mind-calm';
      } else if (outcomeHandle === '2' || outcomeHandle === 'deep-restore') {
        outcomeHandle = 'deep-restore';
      } else if (outcomeHandle === '3' || outcomeHandle === 'circadian-reset') {
        outcomeHandle = 'circadian-reset';
      } else if (outcomeHandle === '4' || outcomeHandle === 'sacred-soothe') {
        outcomeHandle = 'sacred-soothe';
      } else if (!ALLOWED_OUTCOMES.includes(outcomeHandle)) {
        outcomeHandle = 'all';
      }

      // Validate bundle
      let bundlePreference = bundle ? bundle.toLowerCase() : 'unknown';
      if (bundlePreference !== 'unknown' && !ALLOWED_BUNDLES.includes(bundlePreference)) {
        bundlePreference = 'unknown';
      }

      // Build minimal canonical object
      return {
        schema_version: 1,
        outcome_handle: outcomeHandle,
        recommended_category: outcomeHandle,
        bundle_preference: bundlePreference,
        sleep_issue_primary: 'unknown',
        quiz_timestamp: Date.now(),
        answers: {},
        benefits: [],
        email: null
      };
    } catch (e) {
      console.warn('[VelunaQuizState] Error reading from URL:', e);
      return null;
    }
  }

  /**
   * Get quiz state from all sources in priority order
   * @returns {Object|null} - Canonical quiz state or null
   */
  function getQuizState() {
    // Priority 1: Server-provided metafield
    const serverState = readFromServer();
    if (serverState) {
      return serverState;
    }

    // Priority 2: localStorage
    const localStorageState = readFromLocalStorage();
    if (localStorageState) {
      return localStorageState;
    }

    // Priority 3: URL parameters
    const urlState = readFromURL();
    if (urlState) {
      return urlState;
    }

    return null;
  }

  /**
   * Get the source of the current quiz state
   * @returns {string|null} - 'server' | 'localStorage' | 'url' | null
   */
  function getQuizStateSource() {
    if (readFromServer()) {
      return 'server';
    }
    if (readFromLocalStorage()) {
      return 'localStorage';
    }
    if (readFromURL()) {
      return 'url';
    }
    return null;
  }

  // Expose API to window
  window.VelunaQuizState = {
    getQuizState: getQuizState,
    normalizeQuizState: normalizeQuizState,
    getQuizStateSource: getQuizStateSource
  };

})();

